import React from 'react';
import { useTranslation } from 'react-i18next';
// import type { Party } from '@hero/schema';
// import { getObjectId } from '@hero/schema';
import { Form } from '../../../ui/Form';
import { CustomFormField } from '../../../app/CustomFormField';
import { FormFieldTypes } from '@hero/web-types';
import { Button } from '../../../ui/Button';
import { Spinner } from '../../../app/Spinner';
import { ClientRoleSelector } from '../Selector/ClientRoleSelector';
import type { UseClientFormHook } from '@hero/web-hooks';
// import { useClientLinkPickerManager, hitToParty } from '@hero/web-hooks';
// import {
//   EntityDirectoryPicker,
//   type EntityDirectoryPickerSelection,
// } from '../../EntityDirectory';
// import { useNav } from '@hero/web-hooks';

export interface ClientFormProps {
  /** Form hook from `useClientForm()`; required. */
  formHook: UseClientFormHook;
  /** Hide submit button when using modal footer. */
  hideSubmitButton?: boolean;
  isLoading?: boolean;
  /**
   * When true, only render the client name + roles (edit mode).
   * Person/contact fields are seed values for the primary contact and
   * only apply on create.
   */
  isEdit?: boolean;
}

/**
 * Form for creating / editing a `Client` (relations-v2 CRM entity).
 *
 * Replaces the legacy `CustomerForm`. Uses the multi-select
 * `ClientRoleSelector` for `roles[]` instead of the binary
 * Business / Individual switch.
 */
export function ClientForm({
  formHook,
  hideSubmitButton = false,
  isLoading = false,
  isEdit = false,
}: ClientFormProps) {
  const { t } = useTranslation(['m-client', 'c-main']);
  const { form, onValidSubmit, isPersonalClient } = formHook;
  const roles = form.watch('roles') ?? [];
  // const linkedParty = form.watch('linkedParty');
  // const picker = useClientLinkPickerManager();
  // const { navigate } = useNav();

  // const onPick = (selection: EntityDirectoryPickerSelection) => {
  //   if (selection.hit) {
  //     picker.setPickedHit(selection.hit);
  //     const party: Party = hitToParty(selection.hit);
  //     form.setValue('linkedParty', party, { shouldDirty: true });
  //     form.setValue('clientName', selection.hit.name, {
  //       shouldValidate: true,
  //       shouldDirty: true,
  //     });
  //   } else if (selection.createdProfile) {
  //     const profileId = selection.createdProfile._id.toString();
  //     const party: Party = {
  //       _id: getObjectId(profileId),
  //       name: selection.createdProfile.name,
  //       target: 'profile',
  //       roles: ['business_customer'],
  //       ids: [{ type: 'profile_id', id: profileId }],
  //     };
  //     picker.setPickedHit({
  //       name: selection.createdProfile.name,
  //       profileId,
  //       target: 'profile',
  //       isSignedUp: false,
  //     });
  //     form.setValue('linkedParty', party, { shouldDirty: true });
  //     form.setValue('clientName', selection.createdProfile.name, {
  //       shouldValidate: true,
  //       shouldDirty: true,
  //     });
  //   }
  //   picker.closePicker();
  // };

  // const onClearLink = () => {
  //   picker.reset();
  //   form.setValue('linkedParty', null, { shouldDirty: true });
  // };

  /**
   * For a "personal" client (e.g. `individual_customer` selected) the
   * separate company-name field is redundant — the contact person's
   * name IS the client name. The hook mirrors `personName` into
   * `clientName` automatically; we just hide the input so the form
   * stays compact.
   *
   * Edit mode keeps the field visible because person fields aren't
   * editable there.
   */
  const hideClientNameField = !isEdit && isPersonalClient;

  return (
    <Form {...form}>
      <div className="space-y-4">
        <ClientRoleSelector
          value={roles}
          onChange={next =>
            form.setValue('roles', next, { shouldValidate: true, shouldDirty: true })
          }
        />

        {/* Temporarily disabled — pick-a-brand / linked-party flow on create.
        {!isEdit && (
          <div className="rounded-md border border-gray-200 p-3 space-y-2">
            <div className="text-xs text-muted-foreground">
              {t('edit_modal.linked_party.description')}
            </div>
            {linkedParty ? (
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{linkedParty.name}</div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    {linkedParty.target === 'org'
                      ? t('drawer.peek.link_badge.org', { defaultValue: 'Org' })
                      : t('drawer.peek.link_badge.profile', { defaultValue: 'Brand' })}
                  </div>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={onClearLink}>
                  {t('edit_modal.linked_party.unlink', { defaultValue: 'Unlink' })}
                </Button>
              </div>
            ) : (
              <Button type="button" variant="outline" size="sm" onClick={picker.openPicker}>
                {t('edit_modal.linked_party.pick_brand', { defaultValue: 'Pick a brand' })}
              </Button>
            )}
            {picker.existingClient ? (
              <div className="rounded bg-amber-50 border border-amber-200 px-2 py-1.5 text-xs text-amber-800 flex items-center justify-between gap-2">
                <span>
                  {t('modal.client_already_linked', {
                    defaultValue: 'You already have a Client linked to this identity.',
                  })}
                </span>
                <button
                  type="button"
                  className="underline font-medium"
                  onClick={() =>
                    navigate('crm', { clientId: picker.existingClient!._id.toString() })
                  }>
                  {t('modal.open_existing_client', {
                    defaultValue: 'Open existing',
                  })}
                </button>
              </div>
            ) : null}
          </div>
        )}
        */}

        {!hideClientNameField && (
          <CustomFormField
            control={form.control}
            name="clientName"
            filedType={FormFieldTypes.INPUT}
            required
            placeholder={t('form.name.placeholder')}
            label={t('form.name.title')}
          />
        )}

        {/* Contact person — offered, not demanded. `Client.primaryContact`
            is optional on the doc, so marking these `required` here would
            put an asterisk on fields the API happily accepts empty.
            A personal client still needs `personName`, since the hook
            mirrors it into the (required) `clientName`. */}
        {!isEdit && (
          <>
            <CustomFormField
              control={form.control}
              name="personName"
              required={isPersonalClient}
              filedType={FormFieldTypes.INPUT}
              placeholder={t('form.person_name.placeholder')}
              label={t('form.person_name.title')}
            />

            <CustomFormField
              control={form.control}
              name="personPhone"
              filedType={FormFieldTypes.PHONE_INPUT}
              placeholder={t('form.phone_number.placeholder')}
              label={t('form.phone_number.title')}
            />

            <CustomFormField
              control={form.control}
              name="personEmail"
              filedType={FormFieldTypes.INPUT}
              type="email"
              placeholder={t('form.email.placeholder')}
              label={t('form.email.title')}
            />
          </>
        )}

        {!hideSubmitButton && (
          <Button
            type="button"
            className="w-full"
            disabled={isLoading}
            onClick={() => form.handleSubmit(onValidSubmit)()}>
            {isLoading ? (
              <>
                <Spinner className="mr-2" />
                <span>{t('c-main:async_operations.creating')}</span>
              </>
            ) : (
              <span>{t('c-main:crud_buttons.create')}</span>
            )}
          </Button>
        )}
      </div>

      {/* Temporarily disabled — EntityDirectoryPicker for pick-a-brand on create.
      {!isEdit && (
        <EntityDirectoryPicker
          isOpen={picker.isPickerOpen}
          onClose={picker.closePicker}
          onPick={onPick}
          suggestionRoles={roles.length > 0 ? roles : ['business_customer']}
        />
      )}
      */}
    </Form>
  );
}
